import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../../app/store';
import { initializePolicy, setSelectedCompany } from '../../../services/CaseManagementService/policySlice';
import { CardContent, Stack, Box, Typography } from '@mui/material';
import { Policy as PolicyIcon, Business as BusinessIcon } from '@mui/icons-material';
import { PageContainer, PageCard, SectionHeader, SectionPaper } from '../../common/components/styled-ui-elements';
import GoBackButton from '../../common/components/styled-ui-elements/GoBackButton';
import {
  PolicyNumberEditor,
  PolicyNameEditor,
  PolicyCategoryEditor,
  PolicyEffectiveDateEditor,
  PolicyReviewDateEditor,
  PolicySummaryEditor,
  PolicyProcedureCodesEditor,
  PolicyInsurancesEditor
} from '../components/fieldEditors';
import { SavePolicyButton } from '../components/commands';

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

const PolicyCreate: React.FC = () => {
  const query = useQuery();
  const company = query.get('company') || '';

  const dispatch = useAppDispatch();
  const policy = useAppSelector((state) => state.policy.currentPolicy);

  useEffect(() => {
    // Initialize empty policy and set company
    dispatch(initializePolicy());
    if (company) {
      dispatch(setSelectedCompany(company));
    }
  }, [dispatch, company]);

  return (
    <PageContainer>
      <Box display="flex" justifyContent="center" px={1}>
        <PageCard sx={{ minWidth: 800, maxWidth: 1300, width: '100%' }}>
          {/* Professional Header */}
          <Box
            sx={{
              backgroundColor: '#ffffff',
              borderBottom: '2px solid #3b82f6',
              p: 2.5,
              position: 'relative',
            }}
          >
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Box>
                <Typography variant="h5" component="h1" fontWeight={600} color="#1e293b" sx={{ mb: 0.5 }}>
                  Create New Policy
                </Typography>
                <Typography variant="body2" color="#64748b" sx={{ fontSize: '0.9rem' }}>
                  Create a new policy for {company}
                </Typography>
              </Box>
              <Box display="flex" alignItems="center" gap={2}>
                <SavePolicyButton />
                <GoBackButton />
              </Box>
            </Box>
          </Box>
          
          <CardContent sx={{ p: 3, backgroundColor: '#ffffff' }}>
            <form autoComplete="off">
              <Stack spacing={2.5}>
                {/* Effective Procedures & Insurance Section - Moved to Top */}
                <Box>
                  <SectionHeader 
                    icon={<BusinessIcon sx={{ mr: 1, color: '#3b82f6', fontSize: 18 }} />} 
                    title="Effective Procedures & Insurance" 
                  />
                  <SectionPaper>
                    <Stack direction="row" spacing={3} sx={{ p: 2 }}>
                      <Box sx={{ flex: 1 }}>
                        <PolicyProcedureCodesEditor />
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <PolicyInsurancesEditor />
                      </Box>
                    </Stack>
                  </SectionPaper>
                </Box>

                {/* Policy Information Section */}
                <Box>
                  <SectionHeader 
                    icon={<PolicyIcon sx={{ mr: 1, color: '#3b82f6', fontSize: 18 }} />} 
                    title="Policy Information" 
                  />
                  <SectionPaper>
                    <Stack spacing={2.5} sx={{ p: 2 }}>
                      {/* Basic Policy Fields */}
                      <Stack direction="row" spacing={2}>
                        <PolicyNumberEditor />
                        <PolicyNameEditor />
                      </Stack>
                      
                      <Stack direction="row" spacing={2}>
                        <PolicyCategoryEditor />
                        <PolicyEffectiveDateEditor />
                        <PolicyReviewDateEditor />
                      </Stack>

                      {/* Policy Summary */}
                      <PolicySummaryEditor />
                    </Stack>
                  </SectionPaper>
                </Box>
              </Stack>
            </form>
          </CardContent>
        </PageCard>
      </Box>
    </PageContainer>
  );
};

export default PolicyCreate;
